import { FlowsGroup, FlowsGroupNoWaste } from 'hooks/data/datasets/V4/flows/types';
import { groupToTooltipLabel, groupToTooltipTitle } from 'pages/Flows/FlowsView/utils/constants';

import { getExchangeSection } from './exchangesUtils';

/** Maps a v3 exchange `group` onto the V4 FlowsGroup so we can reuse the V4 Flows tab type icons. */
export const exchangeGroupToFlowsGroup = (group: string): FlowsGroup => {
  switch (group) {
    case 'From technosphere':
      return 'valuable';
    case 'Reference product':
      return 'reference flow';
    case 'Waste to treatment':
      return 'waste';
    case 'By-product (Allocatable)':
    case 'By-product (Recyclable)':
    case 'By-product (Waste)':
      return 'by-product';
    case 'From environment':
      return 'resource';
    case 'To environment':
      return 'emission';
    default:
      return 'valuable';
  }
};

/*
 * Waste is the one group V4 leaves out of `groupToTooltipLabel` — hence its `FlowsGroupNoWaste`
 * typing — because the description depends on which way the waste is moving. These are V4's own
 * strings, copied from the identical helpers in FlowsView/FlowViewCard.tsx and
 * FlowGraphView/FlowGraphCard.tsx. Lifting all three into the V4 constants would be the tidier home.
 */
const WASTE_TOOLTIP_BODY: Record<'input' | 'output', string> = {
  input:
    'This flow represents waste materials received by the process from other systems for treatment, recycling, reuse or disposal.',
  output: 'This flow represents a waste material generated by the process that requires disposal, treatment, or management.',
};

/*
 * V4's `by-product` label still holds its placeholder text ("Origin/destination of elementary
 * exchanges: air, soil, water, etc.") — PR #538 rewrote every other entry and missed this one, so it
 * describes compartments rather than by-products. Overridden here in the same voice as the others;
 * delete this once V4's copy is fixed.
 */
const BY_PRODUCT_TOOLTIP_BODY =
  "This flow represents a secondary output produced by the process alongside its reference product. By-products are goods or services that carry a share of the process's environmental burden.";

/** Title and description for an exchange's type icon, matching the V4 Flows tab's tooltips. */
export const getExchangeTooltip = (group: string): { title: string; body: string } => {
  const flowsGroup = exchangeGroupToFlowsGroup(group);

  return { title: groupToTooltipTitle[flowsGroup], body: tooltipBody(group, flowsGroup) };
};

const tooltipBody = (group: string, flowsGroup: FlowsGroup): string => {
  // Only `Waste to treatment` maps to waste today, and INPUT_GROUPS excludes it, so this always
  // resolves to the output copy. Derived rather than hardcoded so it stays right either way.
  if (flowsGroup === 'waste') return WASTE_TOOLTIP_BODY[getExchangeSection(group)];
  if (flowsGroup === 'by-product') return BY_PRODUCT_TOOLTIP_BODY;

  return groupToTooltipLabel[flowsGroup as FlowsGroupNoWaste];
};
